import { useCallback, useEffect, useState } from 'react';
import { Card, DataTable, FilterBar, Modal, FormField, FormSelect, TextInput, Drawer, StatusBadge, EmptyState, PageHeader } from '@/components/ui';
import type { DataTableColumn } from '@/components/ui/DataTable';
import { labelFor } from '@/shared/lib/labels';
import { adminApi } from '@/shared/api/adminApi';
import { toast } from '@/components/ui/Toast';

interface Team {
  id: number;
  name: string;
  description?: string | null;
  color?: string | null;
  manager_id?: number | null;
  manager_name?: string | null;
  members_count?: number;
}

interface Member { id: number; name: string; role: string; extension?: string | null }
interface AgentOpt { id: number; name: string }

const DEFAULT_COLOR = '#5a3df0';
const EMPTY = { name: '', description: '', color: DEFAULT_COLOR, manager_id: '' as string };

export function Teams({ embedded = false }: { embedded?: boolean } = {}) {
  const [rows, setRows] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [agents, setAgents] = useState<AgentOpt[]>([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Team | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Team | null>(null);
  const [membersFor, setMembersFor] = useState<Team | null>(null);
  const [form, setForm] = useState({ ...EMPTY });

  const load = useCallback(() => {
    setLoading(true);
    void adminApi
      .teams(q || undefined)
      .then((res) => setRows(res.data as Team[]))
      .catch(() => toast('שגיאה בטעינת צוותים', 'danger'))
      .finally(() => setLoading(false));
  }, [q]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    void adminApi.agents({ per_page: 200 }).then((res) => setAgents((res.data as AgentOpt[]) ?? [])).catch(() => {});
  }, []);

  async function add() {
    if (!form.name) {
      toast('יש למלא שם', 'danger');
      return;
    }
    try {
      await adminApi.createTeam({
        name: form.name,
        description: form.description || null,
        color: form.color || null,
        manager_id: form.manager_id ? Number(form.manager_id) : null,
      });
      toast('נוסף', 'ok');
      setCreateOpen(false);
      setForm({ ...EMPTY });
      load();
    } catch {
      toast('שגיאה ביצירת צוות', 'danger');
    }
  }

  function openEdit(t: Team) {
    setForm({ name: t.name, description: t.description ?? '', color: t.color || DEFAULT_COLOR, manager_id: t.manager_id ? String(t.manager_id) : '' });
    setEditTarget(t);
  }

  async function saveEdit() {
    if (!editTarget || !form.name) return;
    try {
      await adminApi.updateTeam(editTarget.id, {
        name: form.name,
        description: form.description || null,
        color: form.color || null,
        manager_id: form.manager_id ? Number(form.manager_id) : null,
      });
      toast('עודכן', 'ok');
      setEditTarget(null);
      setForm({ ...EMPTY });
      load();
    } catch {
      toast('שגיאה בעדכון', 'danger');
    }
  }

  async function remove(t: Team) {
    try {
      await adminApi.deleteTeam(t.id);
      toast('נמחק', 'ok');
      setDeleteTarget(null);
      setRows((prev) => prev.filter((r) => r.id !== t.id));
    } catch {
      toast('שגיאה במחיקה', 'danger');
    }
  }

  const cols: DataTableColumn<Team>[] = [
    {
      key: 'name',
      he: 'שם',
      render: (r) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <span style={{ width: 12, height: 12, borderRadius: 3, background: r.color || 'var(--line)', display: 'inline-block' }} />
          {r.name}
        </span>
      ),
    },
    { key: 'description', he: 'תיאור', render: (r) => r.description || '—' },
    { key: 'manager_name', he: 'מנהל', render: (r) => r.manager_name || '—' },
    { key: 'members_count', he: 'חברים', align: 'end', render: (r) => String(r.members_count ?? 0) },
    {
      key: 'actions',
      he: 'פעולות',
      align: 'end',
      render: (r) => (
        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }} onClick={(e) => e.stopPropagation()}>
          <button type="button" className="btn ghost" onClick={() => setMembersFor(r)}>חברים</button>
          <button type="button" className="btn ghost" onClick={() => openEdit(r)}>ערוך</button>
          <button type="button" className="btn danger" onClick={() => setDeleteTarget(r)}>מחק</button>
        </div>
      ),
    },
  ];

  return (
    <div className="stack" style={embedded ? undefined : { padding: 'var(--gap)' }}>
      <PageHeader title="צוותים" actions={<><button type="button" className="btn primary" onClick={() => { setForm({ ...EMPTY }); setCreateOpen(true); }}>+ הוסף צוות</button></>} />
      <FilterBar search={q} onSearch={setQ} onClear={() => setQ('')} placeholder="חיפוש לפי שם או תיאור…" />
      <Card padded={false}><DataTable<Team> columns={cols} rows={rows} loading={loading} onRowClick={openEdit} emptyState={<EmptyState icon="info" title="אין עדיין צוותים" description="צור צוות ראשון כדי לקבץ נציגים יחד." action={<button type="button" className="btn primary" onClick={() => { setForm({ ...EMPTY }); setCreateOpen(true); }}>+ הוסף צוות</button>} />} /></Card>

      <Modal isOpen={createOpen} onClose={() => setCreateOpen(false)} title="צוות חדש"
        footer={<><button type="button" className="btn" onClick={() => setCreateOpen(false)}>ביטול</button><button type="button" className="btn primary" onClick={add}>שמור</button></>}>
        <TeamFields form={form} setForm={setForm} agents={agents} />
      </Modal>

      {editTarget && (
        <Modal isOpen onClose={() => setEditTarget(null)} title={`עריכת ${editTarget.name}`}
          footer={<><button type="button" className="btn" onClick={() => setEditTarget(null)}>ביטול</button><button type="button" className="btn primary" onClick={saveEdit}>שמור</button></>}>
          <TeamFields form={form} setForm={setForm} agents={agents} />
        </Modal>
      )}

      {deleteTarget && (
        <Modal isOpen onClose={() => setDeleteTarget(null)} title="מחיקת צוות"
          footer={<><button type="button" className="btn" onClick={() => setDeleteTarget(null)}>ביטול</button><button type="button" className="btn danger" onClick={() => remove(deleteTarget)}>מחק</button></>}>
          <p>למחוק את הצוות <strong>{deleteTarget.name}</strong>? החברים לא יימחקו, רק השיוך לצוות.</p>
        </Modal>
      )}

      <Drawer isOpen={!!membersFor} onClose={() => { setMembersFor(null); load(); }} title={membersFor ? `חברי ${membersFor.name}` : ''}>
        {membersFor && <MembersManager teamId={membersFor.id} />}
      </Drawer>
    </div>
  );
}

function TeamFields({ form, setForm, agents }: { form: typeof EMPTY; setForm: (f: typeof EMPTY) => void; agents: AgentOpt[] }) {
  return (
    <div className="stack">
      <FormField label="שם" required><TextInput value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></FormField>
      <FormField label="תיאור"><TextInput value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></FormField>
      <FormField label="מנהל הצוות">
        <FormSelect value={form.manager_id} onChange={(e) => setForm({ ...form, manager_id: e.target.value })}>
          <option value="">— ללא —</option>
          {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </FormSelect>
      </FormField>
      <FormField label="צבע">
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            type="color"
            value={/^#[0-9a-fA-F]{6}$/.test(form.color) ? form.color : DEFAULT_COLOR}
            onChange={(e) => setForm({ ...form, color: e.target.value })}
            style={{ width: 44, height: 38, border: '1px solid var(--line)', borderRadius: 'var(--r-2)', background: 'none', cursor: 'pointer' }}
          />
          <TextInput value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} dir="ltr" />
        </div>
      </FormField>
    </div>
  );
}

function MembersManager({ teamId }: { teamId: number }) {
  const [members, setMembers] = useState<Member[]>([]);
  const [available, setAvailable] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    void adminApi
      .teamMembers(teamId)
      .then((d) => {
        setMembers((d.members as Member[]) ?? []);
        setAvailable((d.available as Member[]) ?? []);
      })
      .catch(() => toast('שגיאה בטעינת חברים', 'danger'))
      .finally(() => setLoading(false));
  }, [teamId]);

  useEffect(() => { load(); }, [load]);

  async function sync(ids: number[]) {
    setBusy(true);
    try {
      await adminApi.syncTeamMembers(teamId, ids);
      load();
    } catch {
      toast('שגיאה בעדכון חברים', 'danger');
      setBusy(false);
    }
  }

  function addMember(m: Member) {
    void sync([...members.map((x) => x.id), m.id]);
  }
  function removeMember(m: Member) {
    void sync(members.filter((x) => x.id !== m.id).map((x) => x.id));
  }

  if (loading) return <div style={{ color: 'var(--muted)' }}>טוען…</div>;

  return (
    <div className="stack">
      <div>
        <h4 style={{ margin: '0 0 8px', fontSize: 'var(--t-sm)' }}>חברי הצוות ({members.length})</h4>
        {members.length === 0 ? (
          <div style={{ color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>אין חברים בצוות.</div>
        ) : (
          <div style={{ display: 'grid', gap: 4 }}>
            {members.map((m) => (
              <div key={m.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 12px', background: 'var(--surface-2)', borderRadius: 'var(--r-2)' }}>
                <span>{m.name} <StatusBadge tone="info" size="sm">{labelFor('role', m.role)}</StatusBadge></span>
                <button type="button" className="btn danger" onClick={() => removeMember(m)} disabled={busy}>הסר</button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <h4 style={{ margin: '8px 0', fontSize: 'var(--t-sm)' }}>הוסף נציגים</h4>
        {available.length === 0 ? (
          <div style={{ color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>כל הנציגים כבר בצוות.</div>
        ) : (
          <div style={{ display: 'grid', gap: 4 }}>
            {available.map((m) => (
              <div key={m.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 12px', border: '1px solid var(--line)', borderRadius: 'var(--r-2)' }}>
                <span>{m.name} <span style={{ color: 'var(--muted)', fontSize: 'var(--t-xs)' }}>{labelFor('role', m.role)}</span></span>
                <button type="button" className="btn" onClick={() => addMember(m)} disabled={busy}>הוסף</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
